/**
 * BracketRegion - One region of the bracket (16 teams, R64 through E8).
 *
 * Full-width layout with 4 round columns and SVG connector lines
 * showing game progression from Round of 64 to Elite 8.
 */

import BracketMatchup from './BracketMatchup';

const REGIONS_ORDER = ['East', 'West', 'South', 'Midwest'];

const ROUND_LABELS = {
  1: 'Round of 64',
  2: 'Round of 32',
  3: 'Sweet 16',
  4: 'Elite 8',
};

// Height of one matchup card in px (must match BracketMatchup rendered height).
// Two team rows (40px each) + 1px divider + prediction strip h-4 (16px) + 1px border-t
// + footer h-5 (20px) + 1px border-t + 2px card border = 121px
const MATCHUP_H = 121;
const CONNECTOR_W = 32;
const R1_GAP = 4; // Gap between matchups in Round 1

// Compute gap for each round so that each round's matchups center
// between the pair of matchups they receive winners from.
// R1: gap = R1_GAP
// R2: each matchup centers between 2 R1 matchups → gap = 2*(MATCHUP_H + R1_GAP) - MATCHUP_H = MATCHUP_H + 2*R1_GAP
// General: gap[r] = 2 * (MATCHUP_H + gap[r-1]) - MATCHUP_H
function computeGaps() {
  const gaps = { 1: R1_GAP };
  for (let r = 2; r <= 4; r++) {
    gaps[r] = 2 * (MATCHUP_H + gaps[r - 1]) - MATCHUP_H;
  }
  return gaps;
}

const ROUND_GAPS = computeGaps();

// Top offset for each round so the first matchup centers between its feeders.
// R1: offset = 0
// R2: offset = (MATCHUP_H + R1_GAP) / 2  (half a slot height above first R1 pair midpoint)
// General: offset[r] = offset[r-1] + (MATCHUP_H + gaps[r-1]) / 2
function computeOffsets() {
  const offsets = { 1: 0 };
  for (let r = 2; r <= 4; r++) {
    offsets[r] = offsets[r - 1] + (MATCHUP_H + ROUND_GAPS[r - 1]) / 2;
  }
  return offsets;
}

const ROUND_OFFSETS = computeOffsets();

function BracketRegion({
  region,
  picks,
  entriesMap,
  predictions = {},
  onPickWinner,
  onAnalyze,
  onAskKnewton,
  onMascotBattle,
  onPremiumGate,
  isPremium = false,
  readOnly = false,
  regionColor,
}) {
  // Filter picks for this region using slot-based ranges
  const regionIdx = REGIONS_ORDER.indexOf(region);
  const regionPicks = picks.filter(p => {
    if (p.round_number > 4 || p.round_number < 1) return false;
    const gamesPerRegion = Math.pow(2, 4 - p.round_number); // R1=8, R2=4, R3=2, R4=1
    const slotStart = regionIdx * gamesPerRegion + 1;
    const slotEnd = slotStart + gamesPerRegion - 1;
    return p.game_slot >= slotStart && p.game_slot <= slotEnd;
  });

  const roundGames = {};
  for (let r = 1; r <= 4; r++) {
    roundGames[r] = regionPicks
      .filter(p => p.round_number === r)
      .sort((a, b) => a.game_slot - b.game_slot);
  }

  // Label height (text + margin-bottom)
  const LABEL_H = 28;

  return (
    <div>
      {/* Region header */}
      <div className={`flex items-center gap-3 mb-4 pb-3 border-b border-border/30`}>
        {regionColor && (
          <div className={`w-3 h-3 rounded-full ${regionColor.dot}`} />
        )}
        <h3 className={`text-lg font-bold tracking-wide ${regionColor?.text || 'text-accent-blue'}`}>
          {region} Region
        </h3>
        <div className="text-xs text-text-tertiary">
          {regionPicks.filter(p => p.winner_entry_id).length} / 15 picks
        </div>
      </div>

      {/* Rounds */}
      <div className="overflow-x-auto scrollbar-thin pb-2">
        <div className="flex items-start gap-0 min-w-[700px]">
          {[1, 2, 3, 4].map((roundNum) => {
            const games = roundGames[roundNum] || [];
            const gameCount = Math.pow(2, 4 - roundNum); // 8, 4, 2, 1
            const gapPx = ROUND_GAPS[roundNum];
            const offsetPx = ROUND_OFFSETS[roundNum];

            return (
              <div key={roundNum} className="flex items-start">
                {/* Round column */}
                <div className="flex flex-col shrink-0">
                  {/* Round label */}
                  <div className="text-[10px] text-text-tertiary font-semibold tracking-wider uppercase text-center"
                    style={{ height: `${LABEL_H}px`, lineHeight: `${LABEL_H}px` }}>
                    {ROUND_LABELS[roundNum]}
                  </div>

                  {/* Offset spacer to vertically center with feeder matchups */}
                  {offsetPx > 0 && <div style={{ height: `${offsetPx}px` }} />}

                  {/* Matchups with dynamic spacing */}
                  <div className="flex flex-col" style={{ gap: `${gapPx}px` }}>
                    {games.length > 0 ? (
                      games.map((pick) => (
                        <BracketMatchup
                          key={`${pick.round_number}-${pick.game_slot}`}
                          pick={pick}
                          entriesMap={entriesMap}
                          prediction={predictions[`${pick.round_number}-${pick.game_slot}`]}
                          onPickWinner={onPickWinner}
                          onAnalyze={onAnalyze}
                          onAskKnewton={onAskKnewton}
                          onMascotBattle={onMascotBattle}
                          onPremiumGate={onPremiumGate}
                          isPremium={isPremium}
                          roundNumber={pick.round_number}
                          gameSlot={pick.game_slot}
                          readOnly={readOnly}
                        />
                      ))
                    ) : (
                      Array.from({ length: gameCount }).map((_, i) => (
                        <BracketMatchup
                          key={`empty-${roundNum}-${i}`}
                          pick={null}
                          entriesMap={entriesMap}
                          roundNumber={roundNum}
                          gameSlot={i + 1}
                          readOnly={readOnly}
                        />
                      ))
                    )}
                  </div>
                </div>

                {/* Connector lines between rounds */}
                {roundNum < 4 && (
                  <ConnectorLines
                    gameCount={gameCount}
                    roundNum={roundNum}
                    gapPx={gapPx}
                    offsetPx={offsetPx}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * SVG connector lines between bracket rounds.
 * Draws horizontal + vertical lines connecting pairs of matchups to the next round.
 * Each pair of matchups feeds into one matchup in the next round,
 * with the horizontal output line at the midpoint between the pair.
 */
function ConnectorLines({ gameCount, roundNum, gapPx, offsetPx }) {
  const pairCount = gameCount / 2;
  const slotHeight = MATCHUP_H + gapPx;
  const pairHeight = slotHeight * 2;
  const labelH = 28;

  // Total SVG height: label + offset + all matchup slots
  const totalColumnHeight = labelH + offsetPx + gameCount * MATCHUP_H + (gameCount - 1) * gapPx;
  const svgHeight = totalColumnHeight;

  const lines = [];
  for (let i = 0; i < pairCount; i++) {
    // Y positions relative to top of SVG
    // Each matchup center = labelH + offsetPx + i*slotHeight + MATCHUP_H/2
    const topY = labelH + offsetPx + i * pairHeight + MATCHUP_H / 2;
    const botY = labelH + offsetPx + i * pairHeight + slotHeight + MATCHUP_H / 2;
    const midY = (topY + botY) / 2;

    // Right horizontal from top matchup
    lines.push(
      <line key={`ht-${i}`} x1={0} y1={topY} x2={CONNECTOR_W / 2} y2={topY}
        stroke="currentColor" strokeWidth={1.5} opacity={0.25} />
    );
    // Right horizontal from bottom matchup
    lines.push(
      <line key={`hb-${i}`} x1={0} y1={botY} x2={CONNECTOR_W / 2} y2={botY}
        stroke="currentColor" strokeWidth={1.5} opacity={0.25} />
    );
    // Vertical connecting top to bottom
    lines.push(
      <line key={`v-${i}`} x1={CONNECTOR_W / 2} y1={topY} x2={CONNECTOR_W / 2} y2={botY}
        stroke="currentColor" strokeWidth={1.5} opacity={0.25} />
    );
    // Horizontal to next round (from midpoint)
    lines.push(
      <line key={`hm-${i}`} x1={CONNECTOR_W / 2} y1={midY} x2={CONNECTOR_W} y2={midY}
        stroke="currentColor" strokeWidth={1.5} opacity={0.25} />
    );
  }

  return (
    <svg
      width={CONNECTOR_W}
      height={svgHeight}
      className="shrink-0 text-text-secondary"
    >
      {lines}
    </svg>
  );
}

export default BracketRegion;
